import { Link } from 'react-router-dom';
import { useDashboard } from '@/hooks/useDashboard';
import MainMenu from '@components/ui/game/main-menu/MainMenu';
import Loader from '@/components/ui/general/loader/Loader';
import CreateGameForm from '@components/ui/dashboard/create-game-form';
import { GameStatus } from '@utils/enums';
import StatusTag from '@/components/ui/general/status-tag/StatusTag';
import TurnTag from '@/components/ui/general/turn-tag';
import Tabs from '@/components/ui/Tabs/Tabs';
import './Dashboard.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trash2, User, Bot, Plus } from 'lucide-react';
import JoinGame from "../components/JoinGame";
import { deleteGame } from '@/api/dashboardApi';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import Modal from '@/components/ui/general/Modal';
import Select from '@/components/ui/general/Select';
import useMetadataStore from '@/store/useMetadataStore';
import useConfigStore from '@/store/useConfigStore';
import XhelosTooltip from '@components/ui/general/xhelos-tooltip/XhelosTooltip';
import { updateUserPropertiesApi } from '@/api/userApi';
import useAuthStore from '@/store/useAuthStore';
import { useT } from '@/i18n';
import { usePageBackground } from '@/hooks/usePageBackground';
import useShortcutContext from '@/hooks/useShortcutContext';
import AudioManager from '@audio/AudioManager';

const SHOW_TUTORIAL = import.meta.env.VITE_SHOW_TUTORIAL === 'true';

function Dashboard() {
  const queryClient = useQueryClient();
  const t = useT();
  const [showCreatePlayerModal, setShowCreatePlayerModal] = useState(false);
  const [showDeletePlayerModal, setShowDeletePlayerModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [showDeleteGameModal, setShowDeleteGameModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);

  // Tutorial states
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [showTutorialHint, setShowTutorialHint] = useState(false);
  const tutorialChecked = useRef(false);

  // Shortcut: ESC opens settings (when no modal is open)
  const openSettings = useConfigStore((state) => state.openModal);
  const hasAnyModalOpen = showCreatePlayerModal || showDeletePlayerModal || showDeleteGameModal || showTutorialModal;
  useShortcutContext(
    'page:dashboard',
    { cancel: useCallback(() => openSettings(), [openSettings]) },
    !hasAnyModalOpen
  );

  // ✅ Toda la lógica compleja ahora está contenida aquí
  const {
    user,
    players,
    activePlayer,
    games,
    gameError,
    newPlayerName,
    setNewPlayerName,
    handleCreatePlayer,
    handleSelectPlayer,
    handleCreateGame,
    createPlayerMutation,
    createGameMutation,
    deletePlayerMutation,
  } = useDashboard({
    onPlayerCreated: () => setShowCreatePlayerModal(false)
  });

  const [activeTab, setActiveTab] = useState('create');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Get game config from metadata - redirect to loading if not available
  const metadata = useMetadataStore((state) => state.metadata);

  // Auth store for updating user data locally
  const updateUserData = useAuthStore((state) => state.updateUserData);

  // Mutation for updating user properties (tutorial tracking)
  const updatePropertiesMutation = useMutation({
    mutationFn: (properties) => updateUserPropertiesApi(properties),
    onSuccess: (response) => {
      // Update local user data with new properties
      if (response?.data?.user) {
        updateUserData(response.data.user);
      }
    },
  });

  // Check if should show tutorial on first load
  useEffect(() => {
    if (!SHOW_TUTORIAL || !user || tutorialChecked.current) return;
    tutorialChecked.current = true;

    const userProperties = user.properties || {};
    const tutorialCompleted = userProperties.tutorial_completed === true;
    const loginCount = userProperties.login_count || 0;

    // Increment login count
    const newLoginCount = loginCount + 1;
    updatePropertiesMutation.mutate({ login_count: newLoginCount });

    // Show tutorial modal if never completed
    if (!tutorialCompleted) {
      setShowTutorialModal(true);
    }
    // Show hint if tutorial completed but login count < 5
    else if (newLoginCount <= 5) {
      setShowTutorialHint(true);
    }
  }, [user]);

  // Handle tutorial completion
  const handleTutorialComplete = () => {
    setShowTutorialModal(false);
    setShowTutorialHint(false);
    updatePropertiesMutation.mutate({ tutorial_completed: true });
  };

  // Set dashboard background via Asset system
  usePageBackground('bg.dashboard');

  // Play main theme music if nothing is playing
  useEffect(() => {
    AudioManager.playMusicIfSilent('maintheme');
  }, []);

  // Metadata is loaded in useAuth during login, but show loader as safeguard
  if (!metadata?.game_config) {
    return (
      <div>
        <MainMenu user={null} />
        <div className="dashboard-container">
          <Loader message={t('dashboard.loading_metadata')} />
        </div>
      </div>
    );
  }

  const MAX_PLAYERS = metadata.game_config.max_players_per_user;

  // Si aún no ha cargado la información inicial del usuario
  if (!user)
    return (
      <div>
        <MainMenu user={null} />
        <div className="dashboard-container">
          <h2>{t('dashboard.title')}</h2>
          <Loader message={t('dashboard.loading_user')} />
        </div>
      </div>
    );

  const filteredGames = games.filter(game => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'public') return game.visibility === 'public';
    if (selectedFilter === 'private') return game.visibility === 'private';
    return true;
  });

  // Calculate filter counts
  const totalGames = games.length;
  const publicGames = games.filter(g => g.visibility === 'public').length;
  const privateGames = games.filter(g => g.visibility === 'private').length;

  // Calculate active games count (excluding finished and error games)
  const activeGamesCount = games.filter(game =>
    game.status !== GameStatus.FINISH &&
    game.status !== GameStatus.ERROR
  ).length;

  // Helper to calculate human vs AI players
  const getPlayerCounts = (game) => {
    if (!game.players || game.players.length === 0) {
      return { humans: 0, bots: 0 };
    }

    const humans = game.players.filter(p => p.type === 'human').length;
    const bots = game.players.filter(p => p.type === 'ai').length;

    return { humans, bots };
  };

  const handleDeleteGame = async () => {
    if (!gameToDelete) return;

    const result = await deleteGame(gameToDelete.id);

    if (result) {
      queryClient.invalidateQueries(['games', activePlayer?.id]);
    }

    setShowDeleteGameModal(false);
    setGameToDelete(null);
  };

  // Función para crear jugador y cerrar el modal
  const handleCreatePlayerWithClose = async () => {
    await handleCreatePlayer();
    if (!createPlayerMutation.error) {
      setShowCreatePlayerModal(false);
      setNewPlayerName('');
    }
  };

  return(
    <div className="main-container main-container--dashboard">
      <MainMenu
        showTutorialModal={showTutorialModal}
        onTutorialComplete={handleTutorialComplete}
        showTutorialHint={showTutorialHint}
      />

      <div className="dashboard-container">
        <h2>{t('dashboard.title')}</h2>

        {createPlayerMutation.error && <p className="error-message">{createPlayerMutation.error.userMessage || t('dashboard.error_generic')}</p>}

        {/* ✅ MODAL: Create New Player */}
        <Modal
          isOpen={showCreatePlayerModal}
          onClose={() => setShowCreatePlayerModal(false)}
          title={t('dashboard.create_player')}
        >
          {players.length >= MAX_PLAYERS ? (
            <div className="warning-message">
              {t('dashboard.max_players_reached', { max: MAX_PLAYERS })}
            </div>
          ) : (
            <div className="create-player-form">
              <input
                id="newPlayerName"
                type="text"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreatePlayer()}
                placeholder={t('dashboard.enter_player_name')}
                className="form-input"
                autoFocus
                required
                data-sfx-hover="input"
              />
              <button
                type="button"
                onClick={handleCreatePlayer}
                className="button button-cta"
                disabled={createPlayerMutation.isPending}
                data-sfx="button"
              >
                {createPlayerMutation.isPending ? t('dashboard.creating_player') : t('dashboard.create_player')}
              </button>
            </div>
          )}
        </Modal>

        {players.length === 0 ? (
          <div className="style-glass style-primary">
            <p className="error-message">{t('dashboard.no_players')}</p>
          </div>
        ) : !activePlayer ? (
          <div className="style-glass style-primary">
            <Loader message={t('dashboard.waiting_player_selection')} />
          </div>
        ) : (
          <div className="style-glass style-primary">
            <Tabs
              variant="underline"
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabs={[
                {
                  id: 'create',
                  label: t('dashboard.create_game'),
                  content: (
                    <div className="tab-content">
                      <div className="style-secondary player-selector-row">
                        <label>{t('dashboard.actual_player')}</label>
                        <div className="player-selector-wrapper">
                          <Select
                            value={activePlayer.id}
                            onChange={handleSelectPlayer}
                            options={players}
                            getOptionLabel={(player) => player.name}
                            getOptionValue={(player) => player.id}
                            disabled={players.length <= 1}
                          />

                          {!activePlayer?.can_delete ? (
                            <XhelosTooltip
                              content={activePlayer?.not_delete_reason}
                              placement="bottom"
                              theme="dark"
                              trigger="hover"
                            >
                              <button
                                type="button"
                                className="button-square button-square--delete"
                                disabled={true}
                              >
                                <Trash2 />
                              </button>
                            </XhelosTooltip>
                          ) : (
                            <button
                              type="button"
                              className="button-square button-square--delete"
                              onClick={() => {
                                setPlayerToDelete(activePlayer);
                                setShowDeletePlayerModal(true);
                              }}
                              data-sfx="button"
                            >
                              <Trash2 />
                            </button>
                          )}

                          {players.length >= MAX_PLAYERS ? (
                            <XhelosTooltip
                              content={t('dashboard.max_players_reached', { max: MAX_PLAYERS })}
                              placement="bottom"
                              theme="dark"
                              trigger="hover"
                            >
                              <button
                                type="button"
                                className="button-square"
                                onClick={() => setShowCreatePlayerModal(true)}
                                disabled={true}
                              >
                                <Plus />
                              </button>
                            </XhelosTooltip>
                          ) : (
                            <button
                              type="button"
                              className="button-square"
                              onClick={() => setShowCreatePlayerModal(true)}
                              disabled={false}
                              data-sfx="button"
                            >
                              <Plus />
                            </button>
                          )}
                        </div>
                      </div>

                      <CreateGameForm
                        selectedPlayer={activePlayer.id}
                        onCreateGame={handleCreateGame}
                        error={createGameMutation.error?.userMessage}
                        isLoading={createGameMutation.isPending}
                      />
                    </div>
                  )
                },
                {
                  id: 'games',
                  label: (
                    <span className="tab-label">
                      {t('dashboard.games_tab')}
                      {activeTab !== 'games' && activeGamesCount > 0 && (
                        <span className="games-badge">{activeGamesCount}</span>
                      )}
                    </span>
                  ),
                  content: (
                    <div className="tab-content">
                      <Tabs
                        variant="segmented"
                        className="game-filters"
                        activeTab={selectedFilter}
                        onTabChange={setSelectedFilter}
                        tabs={[
                          { id: 'all', label: t('dashboard.filter_all', { count: totalGames }), content: null },
                          { id: 'public', label: t('dashboard.filter_public', { count: publicGames }), content: null },
                          { id: 'private', label: t('dashboard.filter_private', { count: privateGames }), content: null },
                        ]}
                      />

                      {selectedFilter === 'private' && (
                        <div className="private-code-section">
                          <h3>🔒 {t('dashboard.join_private_title')}</h3>
                          <JoinGame />
                        </div>
                      )}

                      <div className="game-list">
                        {gameError && <p className="error-message">{gameError.message}</p>}
                        {filteredGames.length > 0 ? (
                          <ul>
                            {filteredGames.map(game => {
                              const gameLink = game.status === GameStatus.LOBBY ? `/lobby/${game.id}` : `/games/${game.id}`;
                              const showTurnNumber = game.status !== GameStatus.LOBBY && game.status !== GameStatus.FINISH;
                              const { humans, bots } = getPlayerCounts(game);

                              return (
                                <li className="game-list-item" key={game.id}>
                                  <Link to={gameLink} className="game-link" data-sfx="button">
                                    <div className="game-info-left">
                                      <span className="game-name">
                                        {game.name || t('dashboard.game_default_name', { id: game.id })}
                                      </span>
                                      {game.visibility === "private" && game.access_code && (
                                        <span className="game-code">{t('dashboard.game_code', { code: game.access_code })}</span>
                                      )}
                                    </div>

                                    <div className="game-info-right">
                                      <div className="game-players-group">
                                        <XhelosTooltip
                                          content={humans === 1 ? t('dashboard.human_players_one', { count: humans }) : t('dashboard.human_players_many', { count: humans })}
                                          placement="top"
                                          theme="dark"
                                          trigger="hover"
                                        >
                                          <span className="tag tag-mini tag-mini-humans">
                                            {humans}
                                            <User size={16} />
                                          </span>
                                        </XhelosTooltip>

                                        <XhelosTooltip
                                          content={bots === 1 ? t('dashboard.ai_players_one', { count: bots }) : t('dashboard.ai_players_many', { count: bots })}
                                          placement="top"
                                          theme="dark"
                                          trigger="hover"
                                        >
                                          <span className="tag tag-mini tag-mini-bots">
                                            {bots}
                                            <Bot size={16} />
                                          </span>
                                        </XhelosTooltip>
                                      </div>

                                      <div className="game-status-group">
                                        {showTurnNumber && (
                                          <TurnTag turnNumber={game.turn_number} status={game.status} />
                                        )}

                                        <StatusTag status={game.status} />
                                      </div>
                                    </div>
                                  </Link>
                                  <button
                                    className="button-square button-square--delete"
                                    onClick={() => {
                                      setGameToDelete(game);
                                      setShowDeleteGameModal(true);
                                    }}
                                    data-sfx="button">
                                    <Trash2 />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p>{t('dashboard.no_games')}</p>
                        )}
                      </div>
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </div>

      {/* Delete Player Modal */}
      <Modal
        isOpen={showDeletePlayerModal}
        onClose={() => {
          setShowDeletePlayerModal(false);
          setPlayerToDelete(null);
        }}
        title={t('dashboard.delete_player')}
      >
        <div className="delete-player-modal">
          <p>
            {t('dashboard.confirm_delete_player')}{' '}
            <strong>{playerToDelete?.name}</strong>?
          </p>

          {playerToDelete?.id === activePlayer?.id && players.length > 1 && (
            <p className="warning-message">
              ⚠️ {t('dashboard.active_player_warning')}
            </p>
          )}

          <p className="info-message">
            {t('dashboard.player_deactivate_info')}
          </p>

          <div className="modal-actions">
            <button
              onClick={() => {
                setShowDeletePlayerModal(false);
                setPlayerToDelete(null);
              }}
              className="button button-secondary"
              data-sfx="button"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={() => {
                deletePlayerMutation.mutate(playerToDelete.id);
                setShowDeletePlayerModal(false);
                setPlayerToDelete(null);
              }}
              disabled={deletePlayerMutation.isPending}
              className="button button-danger"
              data-sfx="button"
            >
              {deletePlayerMutation.isPending ? t('dashboard.deleting') : t('dashboard.delete_player')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Game Modal */}
      <Modal
        isOpen={showDeleteGameModal}
        onClose={() => {
          setShowDeleteGameModal(false);
          setGameToDelete(null);
        }}
        title={t('dashboard.delete_game')}
      >
        <div className="delete-game-modal">
          <p>
            {t('dashboard.confirm_delete_game')}{' '}
            <strong>{gameToDelete?.name || `#${gameToDelete?.id}`}</strong>?
          </p>

          <p className="info-message">
            {t('dashboard.action_cannot_be_undone')}
          </p>

          <div className="modal-actions">
            <button
              onClick={() => {
                setShowDeleteGameModal(false);
                setGameToDelete(null);
              }}
              className="button button-secondary"
              data-sfx="button"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDeleteGame}
              className="button button-danger"
              data-sfx="button"
            >
              {t('dashboard.delete_game')}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

export default Dashboard;